import { Box } from '@mui/material'
import { IEntrenamiento } from '.'
import { PaginatorRes } from '../../Pedidos/context/pedidos.context'
import TablaServerPagination from '../../../components/Common/utils/paginator/serverPaginator'
import { useContext, useEffect, useState } from 'react'
import { TenantsContext } from '../../../context/tenants.context'
import ConfirmDialog from '../../Products/components/ConfirmDialog'
import { borrarFotoEntrenamiento, updateFotosEntrenamiento } from '../../AdminProducts/services/rubros.service'
import SnackBarUtils from '../../../utils/SnackBarUtils'
import ExpandImageDialog from '../../Products/components/ExpandImage'
import CardFotos from './cardsFotos'
import CardFotosMobile from './cardsFotosMobile'
import { Rubro } from '../AbmRubros/types/rubro'

interface ListadoFotosProps {
  fotos: IEntrenamiento[]
  paginator: PaginatorRes
  rubros: Rubro[]
  loading: boolean
  setLoading: (l: boolean) => void
  updateData: () => Promise<void>
  isMobile: boolean
}

const ListadoFotos = ({ fotos, paginator, rubros, loading, setLoading, updateData, isMobile }: ListadoFotosProps) => {
  const { tenant } = useContext(TenantsContext)
  const [rubroFotos, setRubroFotos] = useState<Record<number, string[]>>({})
  const [imgSelected, setImgSelected] = useState<string>(null)
  const [expandImg, setExpandImg] = useState<boolean>(false)
  const [confirmDialog, setConfirmDialog] = useState<any>({
    open: false,
    text: {
      title: '',
      body: '',
      leftButton: 'Cancelar',
      rightButton: 'Confirmar',
      colorButtonConfirm: 'primary'
    },
    action: async () => {}
  })

  const clearConfirmDialog = () => {
    setConfirmDialog((confirmDialog: any) => ({
      ...confirmDialog,
      open: false
    }))
  }

  const getRubros = (codRubros: string[]) => {
    return codRubros?.map((c) => rubros.find((r) => r.codRubro === c)).filter(Boolean) as Rubro[]
  }

  const handleRemoveRubro = (fotoId: number, currentCodes: string[], codRubro: string) => {
    const newCodes = currentCodes.filter((c) => c !== codRubro)
    setRubroFotos((prev) => ({
      ...prev,
      [fotoId]: newCodes
    }))
  }
  const handleSave = async (id: number) => {
    try {
      setLoading(true)
      await updateFotosEntrenamiento(tenant, id, rubroFotos[id])
      SnackBarUtils.success('Se guardaron los cambios')
    } catch (e) {
      console.log(e)
      SnackBarUtils.error(e.message === 'Network Error' ? 'Error al actualizar las imágenes' : e.message)
    } finally {
      setLoading(false)
      setRubroFotos({})
      updateData()
    }
  }

  const handleDelete = (id: number) => {
    setConfirmDialog({
      open: true,
      text: {
        title: '¿Está seguro que desea eliminar esta foto?',
        body: 'Se eliminará tanto del servidor S3 como de la base de datos.',
        leftButton: 'Cancelar',
        rightButton: 'Confirmar',
        colorButtonConfirm: 'error'
      },
      action: async () => {
        try {
          setLoading(true)
          await borrarFotoEntrenamiento(tenant, id)
          SnackBarUtils.success('Imagen eliminada correctamente')
        } catch (e) {
          console.log(e)
          SnackBarUtils.error(e.message === 'Network Error' ? 'Ocurrió un error al eliminar imagen' : e.message)
        } finally {
          setLoading(false)
          clearConfirmDialog()
          updateData()
        }
      },
      close: () => clearConfirmDialog()
    })
  }

  const handleAddRubro = (fotoId: number, currentCodes: string[], codRubro: string) => {
    if (!currentCodes.includes(codRubro)) {
      setRubroFotos((prev) => ({
        ...prev,
        [fotoId]: [...currentCodes, codRubro]
      }))
    }
  }

  useEffect(() => {
    if (fotos && fotos.length > 0) {
      const inicial: Record<number, string[]> = {}
      fotos.forEach((f) => {
        inicial[f.idIaEntrenamientoFoto] = f.codRubros || []
      })
      setRubroFotos(inicial)
    }
  }, [fotos])

  const hasChanges = (codes: any[], changes: any[]) => {
    if (codes?.length !== changes?.length) return true
    return !codes.every((valor, i) => valor === changes[i])
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: '1rem',
            ...(!isMobile && { mx: '1rem' })
          }}
        >
          {fotos?.map((f: IEntrenamiento) => {
            const codesActuales = rubroFotos[f.idIaEntrenamientoFoto] ?? f.codRubros
            const rubrosData = getRubros(codesActuales) || []
            const cambios = hasChanges(f.codRubros, rubroFotos[f.idIaEntrenamientoFoto])
            return isMobile ? (
              <CardFotosMobile
                key={f.idIaEntrenamientoFoto}
                foto={f}
                codesActuales={codesActuales}
                rubros={rubros}
                rubrosData={rubrosData}
                cambios={cambios}
                loading={loading}
                setImgSelected={setImgSelected}
                setExpandImg={setExpandImg}
                handleSave={handleSave}
                handleDelete={handleDelete}
                handleAddRubro={handleAddRubro}
                handleRemoveRubro={handleRemoveRubro}
              />
            ) : (
              <CardFotos
                key={f.idIaEntrenamientoFoto}
                foto={f}
                codesActuales={codesActuales}
                rubros={rubros}
                rubrosData={rubrosData}
                cambios={cambios}
                loading={loading}
                setImgSelected={setImgSelected}
                setExpandImg={setExpandImg}
                handleSave={handleSave}
                handleDelete={handleDelete}
                handleAddRubro={handleAddRubro}
                handleRemoveRubro={handleRemoveRubro}
              />
            )
          })}
        </Box>
        <TablaServerPagination label="Rubros por página" paginator={paginator} component={'td'} />
      </Box>
      <ConfirmDialog {...confirmDialog} />
      <ExpandImageDialog
        open={expandImg}
        setExpandImage={setExpandImg}
        imageUrl={imgSelected}
        setImageUrl={setImgSelected}
      />
    </>
  )
}

export default ListadoFotos
